import { HandleSSOCallback, useClerk, useSignIn } from "@clerk/react";
import { isClerkAPIResponseError } from "@clerk/react/errors";
import type { JobStatus, RiskLevel } from "@clarent/scheduling";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { Logo } from "../components/Logo";
import { RiskBadge } from "../components/RiskBadge";
import { currentDestination, rememberDestination, takeRememberedDestination } from "../lib/destination";
import { PROCESS_META, processKind } from "../lib/process";
import { STATUS_META } from "../lib/risk";

/**
 * Decision D31: a custom sign-in page from the design's Login screen. It signs people in with an
 * emailed code, plus Google when the Clerk instance has it on (D34). Sign-up stays in Clerk's own
 * dialog, because the instance asks new accounts for a password.
 */
export function LoginPage() {
  const clerk = useClerk();
  return (
    <div className="flex min-h-screen bg-bg p-4 sm:p-6">
      <div className="flex min-w-0 grow flex-col py-2 sm:pr-12 sm:pl-8">
        <div className="flex h-11 shrink-0 items-center">
          <Logo size={30} wordmark="text-[22px] leading-7" />
        </div>
        <div className="flex grow flex-col justify-center py-12 xl:pl-14">
          <div className="flex w-full max-w-[440px] flex-col gap-9">
            <h1 className="heading flex flex-col gap-1.5 text-[32px] leading-[108%] tracking-[-0.03em] sm:text-[38px]">
              <span>Welcome to Backplan</span>
              <span className="text-quoted">Know when every job has to start</span>
            </h1>
            <SignInForm />
          </div>
        </div>
        <footer className="flex min-h-8 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p className="text-[13px] leading-4 text-muted">
            New to Backplan?{" "}
            <button type="button" className="font-bold text-brand-dark hover:underline" onClick={() => clerk.openSignUp()}>
              Join the free pilot
            </button>
          </p>
          <span className="font-mono text-[11px] leading-[14px] text-subtle">© {new Date().getFullYear()} Backplan</span>
        </footer>
      </div>
      <ProductPanel />
    </div>
  );
}

/** Where Google sends people back to. Clerk finishes the sign-in (or hands a new account to sign-up). */
export function SsoCallback() {
  const clerk = useClerk();
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted">
      Signing you in…
      <HandleSSOCallback
        navigateToApp={({ decorateUrl }) => goTo(decorateUrl(takeRememberedDestination()), navigate)}
        navigateToSignIn={() => navigate({ to: "/" })}
        navigateToSignUp={() => {
          void navigate({ to: "/" });
          clerk.openSignUp();
        }}
      />
    </div>
  );
}

type Navigate = ReturnType<typeof useNavigate>;

function goTo(url: string, navigate: Navigate) {
  if (url.startsWith("http")) window.location.href = url;
  else void navigate({ href: url });
}

const field =
  "h-12 w-full rounded-full border border-line bg-bg px-[18px] text-[15px] text-ink outline-none placeholder:text-subtle focus:border-2 focus:border-brand focus:px-[17px] focus:ring-4 focus:ring-brand-tint";
const primary = "btn-primary h-12 w-full text-[15px]";

/**
 * Decision D34: whether the Clerk instance lets people sign in with Google. Clerk only exposes its
 * instance settings through the untyped `__internal_environment`, so anything unexpected there
 * reads as off and the button stays hidden rather than failing when clicked.
 */
function googleSignInEnabled(clerk: ReturnType<typeof useClerk>): boolean {
  const environment = (clerk as { __internal_environment?: { userSettings?: { authenticatableSocialStrategies?: unknown } } }).__internal_environment;
  const strategies = environment?.userSettings?.authenticatableSocialStrategies;
  return Array.isArray(strategies) && strategies.includes("oauth_google");
}

function SignInForm() {
  const clerk = useClerk();
  const showGoogle = googleSignInEnabled(clerk);
  const navigate = useNavigate();
  const { signIn, fetchStatus } = useSignIn();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<ReactNode>(null);
  const busy = fetchStatus === "fetching";

  const describe = (err: unknown): ReactNode => {
    const first = isClerkAPIResponseError(err) ? err.errors[0] : undefined;
    switch (first?.code) {
      case "form_identifier_not_found":
        return (
          <>
            There's no Backplan account for that email.{" "}
            <button type="button" className="font-bold underline" onClick={() => clerk.openSignUp()}>
              Join the free pilot
            </button>
          </>
        );
      case "form_code_incorrect":
        return "That code isn't right. Check the email and try again.";
      case "verification_expired":
        return "That code has expired. Send a new one.";
      default:
        return first?.longMessage ?? first?.message ?? "Something went wrong. Try again.";
    }
  };

  const sendCode = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (step === "email") {
      const created = await signIn.create({ identifier: email.trim() });
      if (created.error) return setError(describe(created.error));
    }
    const sent = await signIn.emailCode.sendCode();
    if (sent.error) return setError(describe(sent.error));
    setCode("");
    setStep("code");
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const verified = await signIn.emailCode.verifyCode({ code });
    if (verified.error) return setError(describe(verified.error));
    if (signIn.status !== "complete") {
      return setError(
        <>
          This account needs another sign-in step.{" "}
          <button type="button" className="font-bold underline" onClick={() => clerk.openSignIn()}>
            Finish signing in
          </button>
        </>,
      );
    }
    const done = await signIn.finalize({
      navigate: ({ session, decorateUrl }) => {
        // A session with a task to finish (choosing a workspace) stays put: AppShell shows the task.
        if (session.currentTask) return;
        goTo(decorateUrl(currentDestination()), navigate);
      },
    });
    if (done.error) setError(describe(done.error));
  };

  const google = async () => {
    setError(null);
    rememberDestination();
    const { error: err } = await signIn.sso({ strategy: "oauth_google", redirectUrl: currentDestination(), redirectCallbackUrl: "/sso-callback" });
    if (err) setError(describe(err));
  };

  if (step === "code") {
    return (
      <form onSubmit={verify} className="flex flex-col gap-3">
        <p className="text-[15px] leading-[150%] text-muted">
          We've emailed a 6-digit code to <span className="font-bold text-ink">{email.trim()}</span>.
        </p>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="code" className="text-[13px] leading-4 font-bold">
            Sign-in code
          </label>
          <input
            id="code"
            className={`${field} font-mono tracking-[0.2em]`}
            value={code}
            // Digits only, so a pasted " 123456" or "123 456" still fits. No maxLength: the browser
            // would cut a paste down before the spaces come out.
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
          />
        </div>
        {error && <p className="notice-error">{error}</p>}
        <button type="submit" className={primary} disabled={busy || code.length < 6}>
          {busy ? "Checking…" : "Sign in"}
        </button>
        <div className="flex justify-between pt-0.5 text-[13px] leading-[150%]">
          <button
            type="button"
            className="font-bold text-brand-dark hover:underline"
            onClick={() => {
              setError(null);
              setStep("email");
            }}
          >
            Use a different email
          </button>
          <button type="button" className="font-bold text-brand-dark hover:underline disabled:text-subtle" disabled={busy} onClick={() => sendCode()}>
            Send a new code
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={sendCode} className="flex flex-col gap-3">
      {showGoogle && (
        <>
          <button type="button" className="btn-secondary h-12 gap-2.5 text-[15px]" onClick={google} disabled={busy}>
            <GoogleMark />
            Continue with Google
          </button>
          <div className="flex items-center gap-3 py-1.5" aria-hidden="true">
            <span className="h-px grow bg-line" />
            <span className="text-[13px] leading-4 text-subtle">or</span>
            <span className="h-px grow bg-line" />
          </div>
        </>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="email" className="text-[13px] leading-4 font-bold">
          Work email
        </label>
        <input id="email" type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
      </div>
      {error && <p className="notice-error">{error}</p>}
      <button type="submit" className={primary} disabled={busy}>
        {busy ? "Sending…" : "Continue with email"}
      </button>
      <p className="pt-0.5 text-[13px] leading-[150%] text-muted">We'll email you a 6-digit code. No password to remember.</p>
    </form>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
      <path d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" fill="#FFC107" />
      <path d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" fill="#FF3D00" />
      <path d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" fill="#4CAF50" />
      <path d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" fill="#1976D2" />
    </svg>
  );
}

type PreviewCard = { reference: string; part: string; customer: string; process: string; days: number; risk: RiskLevel; date: string };

/** Illustration only: a fictional shop's board, as drawn in the design. */
const PREVIEW: Array<{ status: JobStatus; count: number; cards: PreviewCard[] }> = [
  {
    status: "quoted",
    count: 2,
    cards: [
      { reference: "Q-1182", part: "Hydraulic block", customer: "Ardent Hydraulics", process: "Anodise", days: 3, risk: "ok", date: "Start by Fri 30 Oct" },
      { reference: "Q-1185", part: "Bracket set ×80", customer: "Tyne Marine", process: "Powder coat", days: 2, risk: "tight", date: "Start by Wed 21 Oct" },
    ],
  },
  {
    status: "scheduled",
    count: 3,
    cards: [
      { reference: "JOB-2469", part: "Sensor bracket ×150", customer: "Halcyon Sensors", process: "Powder coat", days: 2, risk: "at_risk", date: "Start by Fri 16 Oct" },
      { reference: "JOB-2481", part: "Manifold block ×40", customer: "Moorside Pumps", process: "Paint", days: 5, risk: "ok", date: "Start by Tue 27 Oct" },
    ],
  },
  {
    status: "in_progress",
    count: 2,
    cards: [
      { reference: "JOB-2461", part: "Manifold plate", customer: "Moorside Pumps", process: "Anodise", days: 3, risk: "ok", date: "Due Fri 23 Oct" },
      { reference: "JOB-2458", part: "Gear blank ×24", customer: "Kestrel Drives", process: "Heat treat", days: 4, risk: "ok", date: "Due Tue 27 Oct" },
    ],
  },
];

function ProductPanel() {
  return (
    <div aria-hidden="true" className="relative hidden w-[54%] max-w-[780px] shrink-0 overflow-clip rounded-xl bg-brand lg:block">
      <div className="absolute top-[23.5%] left-24 flex w-[980px] origin-top-left -rotate-4 flex-col overflow-clip rounded-panel bg-surface shadow-float">
        <div className="flex items-center justify-between border-b border-line bg-bg px-[18px] py-3.5">
          <div className="flex items-center gap-3">
            <span className="heading text-lg leading-[22px]">Board</span>
            <span className="text-[13px] leading-4 text-muted">Northgate CNC · Mon 19 Oct</span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex gap-0.5 rounded-full bg-surface p-[3px] text-[13px] leading-4 font-semibold text-muted">
              <span className="rounded-full bg-bg px-3.5 py-1.5 text-ink shadow-raised">Board</span>
              <span className="px-3.5 py-1.5">Calendar</span>
              <span className="px-3.5 py-1.5">Alerts</span>
            </div>
            <span className="btn-primary">+ New job</span>
          </div>
        </div>
        <div className="flex gap-3.5 px-4 pt-4 pb-7">
          {PREVIEW.map((column) => (
            <div key={column.status} className="flex w-[220px] shrink-0 flex-col gap-2.5">
              <div className="flex items-center gap-2 px-1 py-0.5">
                <span className={`size-2.5 shrink-0 rounded-[3px] ${STATUS_META[column.status].marker}`} />
                <span className="text-[13px] leading-4 font-bold">{STATUS_META[column.status].label}</span>
                <span className="font-mono text-[11px] leading-[14px] text-muted">{column.count}</span>
              </div>
              {column.cards.map((card) => (
                <div key={card.reference} className="card flex flex-col gap-2 p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] leading-3 text-muted">{card.reference}</span>
                    <RiskBadge risk={card.risk} />
                  </div>
                  <div className="flex flex-col gap-px">
                    <span className="text-sm leading-[18px] font-bold">{card.part}</span>
                    <span className="text-xs leading-4 text-muted">{card.customer}</span>
                  </div>
                  <div className="flex">
                    <span className={`tag ${PROCESS_META[processKind(card.process)].tag}`}>
                      {card.process} +{card.days}d
                    </span>
                  </div>
                  <span className={`font-mono text-[11px] leading-[14px] ${card.risk === "at_risk" ? "text-risk-text" : "text-ink"}`}>{card.date}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-[15.5%] left-[470px] flex origin-top-left rotate-3 flex-col gap-1 rounded-[16px] bg-accent px-[18px] py-3.5 shadow-[0_6px_0_var(--color-accent-dark)]">
        <span className="font-mono text-[11px] leading-[14px] font-bold">LATEST START · JOB-2481</span>
        <span className="heading text-[26px] leading-none tracking-[-0.03em]">Tue 27 Oct</span>
      </div>
      <div className="absolute top-[79.3%] left-[72px] flex w-[380px] origin-top-left -rotate-4 items-start gap-3 rounded-panel border-2 border-ink bg-bg px-[18px] py-4 shadow-hard">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-risk">
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M9 4V10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            <circle cx="9" cy="13.5" r="1.3" fill="#fff" />
          </svg>
        </span>
        <div className="flex min-w-0 grow flex-col gap-[3px]">
          <div className="flex justify-between">
            <span className="text-[15px] leading-[18px] font-extrabold">JOB-2469 is late to start</span>
            <span className="font-mono text-[11px] leading-[14px] text-muted">07:00</span>
          </div>
          <span className="text-sm leading-[145%] text-muted">Due Thu 22 Oct, including 2 days at the powder coater.</span>
        </div>
      </div>
    </div>
  );
}
